'use client';

import { useEffect, useState } from 'react';


const LIVE_STATS = [
  { label: 'Active Streams', value: '1,284', unit: 'live now', color: '#e63946' },
  { label: 'Concurrent Viewers', value: '847K', unit: 'watching', color: '#f4a261' },
  { label: 'Avg. Stream Health', value: '99.2%', unit: 'uptime', color: '#4ade80' },
  { label: 'Broadcasters Online', value: '392', unit: 'active', color: '#60a5fa' },
];

const FEATURES = [
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="2" />
        <path d="M4.93 4.93l1.41 1.41" />
        <path d="M17.66 17.66l1.41 1.41" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
        <path d="M6.34 17.66l-1.41 1.41" />
        <path d="M19.07 4.93l-1.41 1.41" />
        <path d="M4.93 19.07l1.41-1.41" />
        <path d="M17.66 6.34l1.41-1.41" />
      </svg>
    ),
    title: 'Real-Time Stream Monitoring',
    description: 'Track bitrate, latency, and viewer counts across all live broadcasts instantly.',
  },
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Broadcaster Management',
    description: 'Manage accounts, permissions, and channel configurations from one panel.',
  },
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Content Moderation',
    description: 'Review flagged content, manage reports, and enforce community guidelines.',
  },
];

export default function BrandPanel() {
  const [tick, setTick] = useState(0);
  const [viewerCount, setViewerCount] = useState(847312);

  // Simulate live viewer count fluctuation
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
      setViewerCount((v) => v + Math.floor(Math.random() * 200) - 80);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const formattedViewers =
    viewerCount >= 1000000
      ? `${(viewerCount / 1000000)?.toFixed(2)}M`
      : `${(viewerCount / 1000)?.toFixed(1)}K`;

  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden bg-[#080a0e]">
      {/* Background gradient mesh */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(230,57,70,0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 80% 80%, rgba(244,162,97,0.05) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      {/* Grid lines overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 flex flex-col h-full px-10 xl:px-14 py-10">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-auto">
          <div className="relative signal-ring">
            <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center glow-red-sm flex-shrink-0">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
                aria-hidden="true"
              >
                <path d="M2 20 L6 16" />
                <path d="M6 20 L6 16" />
                <path d="M10 20 L10 12" />
                <path d="M14 20 L14 8" />
                <path d="M18 20 L18 4" />
                <circle cx="21" cy="4" r="1" fill="currentColor" stroke="none" />
              </svg>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[19px] font-semibold tracking-tight text-white">
                Antrayu<span className="text-[hsl(var(--primary))]">Live</span>
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-medium tracking-widest uppercase bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20">
                <span
                  className="w-1 h-1 rounded-full bg-[hsl(var(--primary))] animate-pulse"
                  aria-hidden="true"
                />
                Admin
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 tracking-widest uppercase font-medium leading-none mt-1">
              Broadcasting Platform
            </p>
          </div>
        </div>

        {/* Hero copy */}
        <div className="mt-16 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/20 mb-5">
            <span
              className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse"
              aria-hidden="true"
            />
            <span className="text-[11px] font-medium text-[hsl(var(--primary))] tracking-wide uppercase">
              Control Panel v3.4.1
            </span>
          </div>
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight text-balance mb-4">
            Command every
            <br />
            broadcast,{' '}
            <span
              className="text-transparent bg-clip-text"
              style={{
                backgroundImage: 'linear-gradient(135deg, #e63946 0%, #f4a261 100%)',
              }}
            >
              live.
            </span>
          </h1>
          <p className="text-[15px] text-zinc-400 leading-relaxed max-w-[360px]">
            Monitor active streams, manage broadcasters, and keep your platform running at
            peak performance — all from a single control panel.
          </p>
        </div>

        {/* Live stats grid */}
        <div className="grid grid-cols-2 gap-3 mb-10">
          {LIVE_STATS?.map((stat, i) => (
            <div
              key={stat?.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 backdrop-blur-sm"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div
                className="text-xl xl:text-2xl font-bold tabular-nums tracking-tight mb-0.5"
                style={{ color: stat?.color }}
              >
                {i === 1 ? formattedViewers : stat?.value}
              </div>
              <div className="text-[12px] font-medium text-zinc-400 leading-tight">
                {stat?.label}
              </div>
              <div className="text-[11px] text-zinc-600 mt-0.5">{stat?.unit}</div>
            </div>
          ))}
        </div>

        {/* Feature list */}
        <div className="space-y-4 mb-10">
          {FEATURES?.map((feature) => (
            <div key={feature?.title} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-white/[0.05] border border-white/[0.07] flex items-center justify-center text-zinc-400 mt-0.5">
                {feature?.icon}
              </div>
              <div>
                <p className="text-[13px] font-semibold text-zinc-200 leading-snug">
                  {feature?.title}
                </p>
                <p className="text-[12px] text-zinc-500 leading-relaxed mt-0.5">
                  {feature?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-auto pt-6 border-t border-white/[0.05]">
          <p className="text-[11px] text-zinc-600 leading-relaxed">
            Restricted to authorized administrators only. Unauthorized access attempts
            are logged and reported.{' '}
            <span className="text-zinc-500">© 2026 AntrayuLive Inc.</span>
          </p>
        </div>
      </div>
    </div>
  );
}